/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// ─── Review Timeline Stepper ──────────────────────────────────────────────────
// Dynamic circular progress stepper driven by department registry.
// Renders: <Dept1> → <Dept2> → ... → DONE
// Used in both Manager Summary View and Admin Review Drawer.

import React, { useMemo } from 'react';
import { Check, Clock, Ban } from 'lucide-react';
import { cn } from '../../ui/utils';
import { DEPARTMENT_STAGES, DepartmentStage } from '../../domain/entities';
import { ReviewAction, SubmissionStatus } from './types';

// ── Color palette that cycles for any number of departments ──────────────────
const COLOR_PALETTE: { ring: string; bg: string; text: string }[] = [
  { ring: 'ring-amber-400',   bg: 'bg-amber-500',   text: 'text-amber-700' },
  { ring: 'ring-blue-400',    bg: 'bg-blue-500',     text: 'text-blue-700' },
  { ring: 'ring-purple-400',  bg: 'bg-purple-500',   text: 'text-purple-700' },
  { ring: 'ring-teal-400',    bg: 'bg-teal-500',     text: 'text-teal-700' },
  { ring: 'ring-rose-400',    bg: 'bg-rose-500',     text: 'text-rose-700' },
  { ring: 'ring-indigo-400',  bg: 'bg-indigo-500',   text: 'text-indigo-700' },
  { ring: 'ring-orange-400',  bg: 'bg-orange-500',   text: 'text-orange-700' },
  { ring: 'ring-cyan-400',    bg: 'bg-cyan-500',     text: 'text-cyan-700' },
];

const DONE_COLORS = { ring: 'ring-emerald-400', bg: 'bg-emerald-500', text: 'text-emerald-700' };

function buildNodeColors(stages: string[]): Record<string, { ring: string; bg: string; text: string; label: string }> {
  const colors: Record<string, { ring: string; bg: string; text: string; label: string }> = {};
  stages.forEach((stage, i) => {
    const palette = COLOR_PALETTE[i % COLOR_PALETTE.length];
    colors[stage] = { ...palette, label: stage };
  });
  colors['DONE'] = { ...DONE_COLORS, label: 'Done' };
  return colors;
}

interface ReviewTimelineProps {
  currentStage: DepartmentStage;
  status: SubmissionStatus;
  reviewHistory: ReviewAction[];
  /** Dynamic department stages from registry. Falls back to legacy DEPARTMENT_STAGES if omitted. */
  stages?: string[];
  /** Compact mode for inline use */
  compact?: boolean;
}

type NodeState = 'completed' | 'active' | 'rejected' | 'pending';

function getNodeState(
  node: string,
  allNodes: string[],
  currentStage: DepartmentStage,
  status: SubmissionStatus,
  _reviewHistory: ReviewAction[],
): NodeState {
  const nodeIdx = allNodes.indexOf(node);
  const currentIdx = allNodes.indexOf(currentStage);

  // Rejected — mark the current stage as rejected, everything after as pending
  if (status === SubmissionStatus.REJECTED) {
    if (nodeIdx < currentIdx) return 'completed';
    if (nodeIdx === currentIdx) return 'rejected';
    return 'pending';
  }

  // COMPLETED or APPROVED — everything done
  if (status === SubmissionStatus.COMPLETED || status === SubmissionStatus.APPROVED) {
    return 'completed';
  }

  // "DONE" is a virtual final node
  if (node === 'DONE') {
    return currentStage === 'DONE' ? 'completed' : 'pending';
  }

  // If submission is fully approved → everything completed
  if (currentStage === 'DONE') return 'completed';

  // If the node is before the current stage → completed
  if (nodeIdx < currentIdx) return 'completed';

  // Current node
  if (nodeIdx === currentIdx) {
    return 'active';
  }

  // Future node
  return 'pending';
}

export function ReviewTimeline({ currentStage, status, reviewHistory, stages, compact }: ReviewTimelineProps) {
  // Build the full node list: [...department stages, 'DONE']
  const deptStages = stages && stages.length > 0 ? stages : [...DEPARTMENT_STAGES];
  const allNodes = useMemo(() => [...deptStages, 'DONE'], [deptStages]);
  const nodeColors = useMemo(() => buildNodeColors(deptStages), [deptStages]);

  const size = compact ? 'w-8 h-8' : 'w-10 h-10';
  const iconSize = compact ? 14 : 16;
  const fontSize = compact ? 'text-[9px]' : 'text-[10px]';

  return (
    <div className="flex items-center justify-center gap-0">
      {allNodes.map((node, i) => {
        const state = getNodeState(node, allNodes, currentStage, status, reviewHistory);
        const colors = nodeColors[node] ?? { ring: 'ring-zinc-400', bg: 'bg-zinc-500', text: 'text-zinc-700', label: node };

        return (
          <React.Fragment key={node}>
            {/* Node circle */}
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={cn(
                  size,
                  'rounded-full flex items-center justify-center transition-all duration-300 relative',
                  state === 'completed' && 'bg-emerald-500 text-white',
                  state === 'active' && cn(colors.bg, 'text-white ring-4 ring-offset-2', colors.ring, 'animate-timeline-pulse'),
                  state === 'rejected' && 'bg-zinc-800 text-white ring-4 ring-offset-2 ring-zinc-400',
                  state === 'pending' && 'bg-zinc-200 text-zinc-400',
                )}
              >
                {state === 'completed' && <Check size={iconSize} strokeWidth={3} />}
                {state === 'active' && <Clock size={iconSize} />}
                {state === 'rejected' && <Ban size={iconSize} strokeWidth={3} />}
                {state === 'pending' && <span className={cn('font-bold', fontSize)}>{i + 1}</span>}
              </div>
              <span className={cn(
                'font-bold uppercase tracking-widest',
                fontSize,
                state === 'completed' && 'text-emerald-600',
                state === 'active' && colors.text,
                state === 'rejected' && 'text-zinc-700',
                state === 'pending' && 'text-zinc-400',
              )}>
                {colors.label}
              </span>
            </div>

            {/* Connector line */}
            {i < allNodes.length - 1 && (
              <div className={cn(
                'flex-1 min-w-6 max-w-12 h-0.5 mx-1 rounded-full transition-all duration-300',
                (() => {
                  const nextNode = allNodes[i + 1];
                  const nextState = getNodeState(nextNode, allNodes, currentStage, status, reviewHistory);
                  if (state === 'completed' && (nextState === 'completed' || nextState === 'active')) return 'bg-emerald-400';
                  return 'bg-zinc-200';
                })(),
              )} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
